import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createApiClient } from '@shared/api/client/api-client';
import { createAuthApi } from '@shared/api/lib/auth-api';
import { mapAuthResponseDtoToEntity } from '@entities/auth';
import { useAuth } from '@entities/auth';
import { applyAuthResponse } from '@entities/auth';
import {
  trackAuthLoginAttempted,
  trackAuthLoginFailed,
  trackAuthLoginSuccess,
} from '@shared/lib/analytics';
import { setRememberMe, setRememberedEmail } from '@shared/lib/remember-me';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';
import { isApiError } from '@shared/api/errors/errors';
import type { AuthAction, SecondFactorMethod } from '@entities/auth';
import { isMemoryChallengeAction } from '@entities/security';
import { readApiError } from '@shared/api/errors/read-api-error';

import { env } from '@shared/config/env';

interface UseLoginResult {
  isLoading: boolean;
  error: string | null;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<{
    success: boolean;
    action?: AuthAction;
    error?: string;
    challenge?: {
      token: string;
      methods: SecondFactorMethod[];
      sentTo?: string | null;
      resendAfterSec?: number;
    };
    lockedUntil?: string;
  }>;
}

export function useLogin(): UseLoginResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation('auth');
  const { login: authLogin } = useAuth();

  const login = async (
    email: string,
    password: string,
    rememberMe = false,
  ): Promise<{
    success: boolean;
    action?: AuthAction;
    error?: string;
    challenge?: {
      token: string;
      methods: SecondFactorMethod[];
      sentTo?: string | null;
      resendAfterSec?: number;
    };
    lockedUntil?: string;
  }> => {
    setIsLoading(true);
    setError(null);
    trackAuthLoginAttempted();
    setRememberMe(rememberMe);

    try {
      const apiClient = createApiClient({ baseURL: env.API_BASE_URL });
      const authApi = createAuthApi(apiClient);

      const response = await authApi.login({ email, password });
      const authResponse = mapAuthResponseDtoToEntity(response);
      const { action } = await applyAuthResponse(authResponse, authLogin, { rememberMe });
      setRememberedEmail(rememberMe ? email : null);
      trackAuthLoginSuccess();

      return {
        success: true,
        action,
        ...(isMemoryChallengeAction(action)
          ? {
              challenge: {
                token: authResponse.tokens.accessToken,
                methods: authResponse.methods ?? [],
                sentTo: authResponse.sentTo,
                resendAfterSec: authResponse.resendAfterSec,
              },
            }
          : {}),
      };
    } catch (err: unknown) {
      const parsed = readApiError(err);
      if (parsed?.code === 'LOGIN_LOCKED' || parsed?.code === 'ENVIRONMENT_CHANGED') {
        const errorMessage = t('security.loginLocked');
        trackAuthLoginFailed('login_failed');
        setError(errorMessage);
        return { success: false, error: errorMessage, lockedUntil: parsed.lockedUntil };
      }
      const errorMessage =
        isApiError(err) && (err.status === 401 || err.status === 400)
          ? t('errors.loginFailed')
          : getApiErrorMessage(err, t, 'errors.loginFailed');
      trackAuthLoginFailed('login_failed');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, error, login };
}
